/**
 * htmlText — HTML entity decoding and tag stripping.
 * --------------------------------------------------------------------------
 * Extracted from newsParser so the feed normaliser and the article extractor
 * share ONE implementation. They were about to diverge, and a decoder that is
 * subtly different between the card and the reader shows up as the same
 * headline rendering two ways on two screens.
 *
 * Deliberately string-based: no DOMParser, no `document`. This runs in the
 * browser, in the Android WebView, under Node in the test suite, and would run
 * unchanged in React Native — which has no DOM at all.
 */

/**
 * Named HTML entities that actually turn up in the feeds and article pages we
 * consume. Numeric entities (&#039; &#x2019;) are handled generically below.
 */
export const NAMED_ENTITIES = {
  amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ',
  hellip: '…', mdash: '—', ndash: '–', minus: '−',
  lsquo: '‘', rsquo: '’', ldquo: '“', rdquo: '”',
  laquo: '«', raquo: '»', bull: '•', middot: '·', deg: '°',
  copy: '©', reg: '®', trade: '™', euro: '€', pound: '£', times: '×',
  eacute: 'é', egrave: 'è', ecirc: 'ê', agrave: 'à', acirc: 'â',
  uuml: 'ü', ouml: 'ö', auml: 'ä', ccedil: 'ç', ntilde: 'ñ', szlig: 'ß',
  oslash: 'ø', aring: 'å', iacute: 'í', oacute: 'ó', uacute: 'ú', aacute: 'á',
};

function fromCodePoint(code) {
  // Reject out-of-range and surrogate code points rather than throwing.
  if (!Number.isFinite(code) || code < 0 || code > 0x10ffff) return '';
  if (code >= 0xd800 && code <= 0xdfff) return '';
  return String.fromCodePoint(code);
}

/**
 * decodeEntities — feed payloads arrive HTML-escaped, and several feeds are
 * double-escaped (`&amp;#039;`), so we run up to two passes.
 */
export function decodeEntities(text) {
  let out = String(text ?? '');
  for (let pass = 0; pass < 2; pass++) {
    if (!/&(#\d+|#x[0-9a-f]+|[a-z]+);/i.test(out)) break;
    out = out
      .replace(/&#x([0-9a-f]+);/gi, (m, hex) => fromCodePoint(parseInt(hex, 16)) || m)
      .replace(/&#(\d+);/g, (m, dec) => fromCodePoint(parseInt(dec, 10)) || m)
      .replace(/&([a-z]+);/gi, (m, name) => NAMED_ENTITIES[name.toLowerCase()] ?? m);
  }
  return out;
}

/**
 * cleanHtml — HTML fragment to a single line of plain text.
 *
 * Tags are stripped BEFORE decoding so a decoded `<` can never synthesise a
 * tag that the stripper already ran past.
 */
export function cleanHtml(html) {
  if (!html) return '';
  return collapseWhitespace(decodeEntities(String(html).replace(/<[^>]*>/g, '')));
}

/**
 * collapseWhitespace — including the U+00A0 non-breaking spaces that survive
 * entity decoding, which `\s` in older engines does not always cover. Written
 * as an escape rather than a literal so the character stays visible in diffs.
 */
export function collapseWhitespace(text) {
  return String(text ?? '').replace(/[\s\u00a0]+/g, ' ').trim();
}

/**
 * resolveUrl — absolutise a possibly-relative URL against the page it came
 * from. Article pages routinely use `/media/x.jpg` or `//cdn.host/x.jpg`.
 * Returns null for anything unusable so callers can simply drop it.
 */
export function resolveUrl(url, baseUrl) {
  const raw = String(url ?? '').trim();
  if (!raw) return null;
  // data: URIs are already self-contained; anything else non-http is noise.
  if (/^data:image\//i.test(raw)) return raw;
  try {
    const resolved = baseUrl ? new URL(raw, baseUrl) : new URL(raw);
    return resolved.protocol === 'http:' || resolved.protocol === 'https:' ? resolved.href : null;
  } catch {
    return null;
  }
}
